import { useState } from 'react';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AlbumIcon from '@mui/icons-material/Album';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import { useNavigate } from 'react-router-dom';
import { AppRoutes, getDashboardRoute } from '../../constants/routes';
import { useAuthProvider } from '../../utils/auth';

const ACCOUNT_DASHBOARDS = {
  creator: 'Creator dashboard',
  podcaster: 'Podcaster dashboard',
};

const roleDetail = (noun, role) => (role && role !== 'Owner' ? `${noun} · ${role}` : noun);

// The choices for "acting as": the person listening (Me), their own
// creator or podcaster dashboard, and each label and publisher they run
// or work for
export const useActingAsOptions = () => {
  const { user, userType, isListening, workingAs } = useAuthProvider();
  const labels = user?.organizations?.labels || [];
  const publishers = user?.organizations?.publishers || [];
  const label = labels[0];
  const publisher = publishers[0];
  const options = [{ key: 'me', name: 'Me', detail: 'Listening', icon: <HeadphonesIcon fontSize="small" /> }];
  if (ACCOUNT_DASHBOARDS[userType]) {
    options.push({ key: 'account', name: ACCOUNT_DASHBOARDS[userType], icon: <DashboardIcon fontSize="small" /> });
  }
  labels.forEach((each) => options.push({
    key: `label-${each.id}`, name: each.name, detail: roleDetail('Label', each.role),
    icon: <AlbumIcon fontSize="small" />, organization: { kind: 'label', id: each.id, name: each.name },
  }));
  publishers.forEach((each) => options.push({
    key: `publisher-${each.id}`, name: each.name, detail: roleDetail('Publisher', each.role),
    icon: <HistoryEduIcon fontSize="small" />, organization: { kind: 'publisher', id: each.id, name: each.name },
  }));

  let current = 'me';
  if (workingAs) {
    current = `${workingAs.kind}-${workingAs.id}`;
  } else if (!isListening) {
    // An account that signed up as a label or publisher, on its dashboard
    if ((userType === 'label' || userType === 'label_team_member') && label) current = `label-${label.id}`;
    else if (userType === 'publisher' && publisher) current = `publisher-${publisher.id}`;
    else current = 'account';
  }
  return { options, current };
};

const ActingAsSwitcher = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userType, listenAsMe, workAsAccount, workAsOrganization } = useAuthProvider();
  const { options, current } = useActingAsOptions();
  const [anchor, setAnchor] = useState(null);

  // Someone who only listens has nothing to switch to
  if (options.length < 2) return null;
  const selected = options.find((option) => option.key === current) || options[0];

  const choose = (option) => {
    setAnchor(null);
    if (option.key === current) return;
    if (option.key === 'me') {
      listenAsMe();
      navigate(AppRoutes.Home2);
    } else if (option.key === 'account') {
      workAsAccount();
      navigate(getDashboardRoute(userType));
    } else {
      workAsOrganization(option.organization);
      navigate(option.organization.kind === 'label' ? AppRoutes.LabelDash : AppRoutes.PublisherDash);
    }
  };

  return (
    <>
      <Button
        id="acting-as-button"
        aria-controls={anchor ? 'acting-as-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={anchor ? 'true' : undefined}
        onClick={(event) => setAnchor(event.currentTarget)}
        variant="outlined"
        size="small"
        startIcon={isMobile ? null : selected.icon}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          textTransform: 'none',
          borderRadius: '999px',
          maxWidth: { xs: 120, md: 220 },
          whiteSpace: 'nowrap',
          '& .MuiButton-endIcon': { ml: 0.25 },
        }}
        title={`Acting as ${selected.name}`}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected.name}</span>
      </Button>
      <Menu
        id="acting-as-menu"
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        MenuListProps={{ 'aria-labelledby': 'acting-as-button', dense: true }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {options.map((option) => (
          <MenuItem key={option.key} selected={option.key === current} onClick={() => choose(option)}>
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText primary={option.name} secondary={option.detail} />
            {option.key === current && <CheckIcon fontSize="small" sx={{ ml: 2 }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ActingAsSwitcher;
